<template>
  <div class="activity-view">
    <!-- Activity Complete Screen -->
    <div v-if="activityComplete" class="container py-4">
      <div class="row justify-content-center">
        <div class="col-12 col-lg-8">
          <div class="card shadow-sm bg-white border-success">
            <div class="card-body text-center p-5">
              <h2 class="text-pine-stand mb-4">
                {{ scenesData.completionMessage.title }}
              </h2>
              <p class="text-dark mb-4">
                {{ scenesData.completionMessage.message }}
              </p>
              <img
                :src="completionImage"
                alt="Thriving wetland ecosystem"
                class="img-fluid rounded mb-4"
              />

              <!-- Final Educational Content -->
              <div
                v-if="scenesData.completionMessage.finalEducation"
                class="card bg-light mt-4 text-start"
              >
                <div class="card-body">
                  <h5 class="fw-bold text-pine-stand mb-1">
                    {{ scenesData.completionMessage.finalEducation.title }}
                  </h5>
                  <p
                    v-for="(
                      paragraph, index
                    ) in completionFinalEducationParagraphs"
                    :key="`completion-paragraph-${index}`"
                    class="mb-3"
                    :class="{
                      'mb-0':
                        index === completionFinalEducationParagraphs.length - 1
                    }"
                  >
                    {{ paragraph }}
                  </p>
                </div>
              </div>

              <div class="mt-4">
                <p class="fw-bold fs-5 text-dark-blue-grey mb-3">
                  Would you like to do the activity again?
                </p>
                <button
                  class="btn btn-lg fw-bold btn-primary"
                  @click="startOver"
                >
                  Yes, Restart the Activity
                </button>
                <router-link
                  :to="{ name: 'Landing' }"
                  class="btn btn-lg fw-bold btn-outline-dark ms-2"
                >
                  Return to Home
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="viewingEducationalSlide" class="container py-4">
      <div class="row justify-content-center">
        <div class="col-12 col-lg-10">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-high-tide text-white text-start">
              <h2 class="mb-0">{{ educationalSlideTitle }}</h2>
            </div>
            <div class="card-body text-start">
              <div class="row align-items-stretch">
                <!-- Educational Image Column -->
                <div class="col-12 col-md-6 mb-3 mb-md-0">
                  <img
                    :src="educationalSlideImage"
                    :alt="currentScenario.educationalSlide.title"
                    class="img-fluid rounded w-100 d-block"
                  />
                </div>

                <!-- Content Column -->
                <div class="col-12 col-md-6 d-flex flex-column">
                  <p
                    v-for="(paragraph, index) in educationalSlideParagraphs"
                    :key="`${currentScenario.id}-${currentRound.id}-${index}`"
                    class="mb-3"
                  >
                    {{ paragraph }}
                  </p>

                  <!-- Species Highlights -->
                  <div
                    v-if="showEducationalSpeciesHighlights"
                    class="mt-auto pt-3"
                  >
                    <h5 class="fw-bold text-pine-stand mb-2">
                      Other notable species
                    </h5>
                    <div class="row g-2">
                      <div
                        v-for="species in currentScenario.educationalSlide
                          .speciesHighlights"
                        :key="species.name"
                        class="col-6 col-md-3"
                      >
                        <div
                          class="card h-100 border-success notable-species-card"
                        >
                          <div
                            class="species-image-container notable-species-image-container overflow-hidden d-flex align-items-center justify-content-center"
                            v-if="species.image"
                          >
                            <img
                              :src="getSpeciesImage(species.image)"
                              :alt="species.name"
                              class="notable-species-image"
                            />
                          </div>
                          <div class="card-body text-center p-2">
                            <h6 class="card-title mb-0">{{ species.name }}</h6>
                            <small
                              class="text-muted fst-italic d-block scientific-name"
                              >{{ species.scientificName }}</small
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-footer text-center">
              <button
                class="btn btn-lg fw-bold btn-primary"
                @click="handleEducationalSlideContinue"
              >
                {{ educationalSlideContinueText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Management Options View (Scenario 2 only) -->
    <div v-else-if="viewingManagementOptions" class="container py-4">
      <div class="row justify-content-center">
        <div class="col-12 col-lg-10">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-deep-orange text-white text-start">
              <h2 class="mb-0">
                {{ currentScenario.managementOptions.title }}
              </h2>
            </div>
            <div class="card-body text-start bg-light-peach">
              <p class="mb-4">
                {{ currentScenario.managementOptions.prompt }}
              </p>

              <div class="row g-3">
                <div
                  v-for="option in managementOptionsForDisplay"
                  :key="option.id"
                  class="col-12 col-md-6"
                >
                  <button
                    type="button"
                    class="card h-100 management-option w-100 text-start flex-row"
                    :class="{
                      'management-option-selected':
                        selectedManagementOption === option.id
                    }"
                    @click="selectManagementOption(option.id)"
                  >
                    <!-- Option Image -->
                    <div
                      class="species-image-container pt-2 overflow-hidden d-flex align-items-center justify-content-center"
                      style="width: 200px; flex-shrink: 0"
                    >
                      <img
                        :src="getSpeciesImage(option.image)"
                        :alt="option.title"
                        class="w-100 h-100 object-fit-contain rounded-3"
                      />
                    </div>
                    <div class="card-body text-start">
                      <h5 class="card-title fw-bold mb-1">
                        {{ option.title }}
                      </h5>
                      <p class="small text-muted mb-0">
                        {{ option.description }}
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              <div
                v-if="selectedManagementOption"
                class="alert alert-info mt-3 mb-0"
              >
                <h5 class="fw-bold mb-1">Pros and Cons:</h5>
                <p class="mb-0">{{ getSelectedManagementExplanation() }}</p>

                <div
                  v-if="
                    isPreferredManagementOption && isManagementDecisionConfirmed
                  "
                  class="alert alert-success mt-3 mb-0"
                >
                  <p class="mb-0">{{ preferredManagementOptionMessage }}</p>
                </div>

                <div
                  v-if="isPreferredManagementOption"
                  class="mt-3 d-flex align-items-center gap-2"
                >
                  <button
                    v-if="!isManagementDecisionConfirmed"
                    type="button"
                    class="btn btn-sm fw-bold btn-primary"
                    @click="confirmManagementDecision"
                  >
                    Confirm this decision
                  </button>
                  <span v-else class="badge bg-success" aria-live="polite">
                    Decision confirmed
                  </span>
                </div>
              </div>
            </div>
            <div class="card-footer text-center bg-light-peach">
              <button
                class="btn btn-lg fw-bold btn-primary"
                :disabled="!isManagementDecisionConfirmed"
                @click="handleManagementContinue"
              >
                Continue to Restoration →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Round View (Species Selection) -->
    <div v-else>
      <!-- Round Content -->
      <div class="container-lg py-4">
        <!-- Progress Indicator Card - Full Width -->
        <div class="card shadow-sm mb-3 border-0 rounded-3">
          <div class="card-body bg-deep-orange text-light py-2 px-3 rounded-3">
            <div
              class="d-flex flex-column flex-md-row justify-content-between align-items-center gap-1"
            >
              <div class="text-start">
                <h5 class="mb-0 fw-bold lh-sm">{{ currentScenario.title }}</h5>
                <small class="text-light d-block lh-sm">
                  Round {{ currentRoundIndex + 1 }} of
                  {{ totalRoundsInScenario }}
                </small>
              </div>
              <div class="d-flex gap-2">
                <!-- Scenario badges -->
                <div>
                  <small class="text-light d-block mb-0">Scenarios</small>
                  <span
                    v-for="(scenario, sIndex) in scenesData.scenarios"
                    :key="scenario.id"
                    class="badge me-1 py-1 px-2 progress-state-badge"
                    :class="[
                      getProgressBadgeClasses(
                        isScenarioComplete(sIndex),
                        sIndex === currentScenarioIndex
                      ),
                      {
                        'dev-nav-link': isDebugMode
                      }
                    ]"
                    :role="isDebugMode ? 'button' : undefined"
                    :tabindex="isDebugMode ? 0 : undefined"
                    :aria-label="
                      getProgressBadgeAriaLabel(
                        'Scenario',
                        sIndex + 1,
                        isScenarioComplete(sIndex),
                        sIndex === currentScenarioIndex
                      )
                    "
                    @click="navigateToScenario(sIndex)"
                    @keydown.enter="navigateToScenario(sIndex)"
                  >
                    {{ sIndex + 1 }}
                  </span>
                </div>
                <!-- Round badges for current scenario -->
                <div>
                  <small class="text-light d-block mb-0">Rounds</small>
                  <span
                    v-for="(round, rIndex) in currentScenario.rounds"
                    :key="round.id"
                    class="badge me-1 py-1 px-2 progress-state-badge"
                    :class="[
                      getProgressBadgeClasses(
                        isRoundComplete(round.id),
                        rIndex === currentRoundIndex
                      ),
                      {
                        'dev-nav-link': isDebugMode
                      }
                    ]"
                    :role="isDebugMode ? 'button' : undefined"
                    :tabindex="isDebugMode ? 0 : undefined"
                    :aria-label="
                      getProgressBadgeAriaLabel(
                        'Round',
                        rIndex + 1,
                        isRoundComplete(round.id),
                        rIndex === currentRoundIndex
                      )
                    "
                    @click="navigateToRound(rIndex)"
                    @keydown.enter="navigateToRound(rIndex)"
                  >
                    {{ rIndex + 1 }}
                  </span>
                </div>
                <div class="position-relative d-flex align-items-center">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-light fw-bold progress-help-button"
                    aria-controls="progressLegend"
                    :aria-label="
                      showProgressLegend
                        ? 'Hide progress indicator legend'
                        : 'Show progress indicator legend'
                    "
                    :aria-expanded="showProgressLegend"
                    @click="showProgressLegend = !showProgressLegend"
                  >
                    ?
                  </button>
                  <div
                    id="progressLegend"
                    v-show="showProgressLegend"
                    class="progress-legend card border-0 shadow-sm text-start"
                  >
                    <div class="card-body p-2">
                      <p class="fw-bold mb-1 small text-dark-blue-grey">
                        Indicator Legend
                      </p>
                      <div class="small d-flex align-items-center mb-1">
                        <span
                          class="badge py-1 px-2 me-2 progress-state-badge progress-badge-incomplete"
                          >1</span
                        >
                        Incomplete
                      </div>
                      <div class="small d-flex align-items-center mb-1">
                        <span
                          class="badge py-1 px-2 me-2 progress-state-badge progress-badge-complete"
                          >1</span
                        >
                        Complete
                      </div>
                      <div class="small d-flex align-items-center mb-0">
                        <span
                          class="badge py-1 px-2 me-2 progress-state-badge progress-badge-incomplete progress-badge-active"
                          >1</span
                        >
                        Active (yellow outline)
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Scenario & Task Card - Full Width (Collapsible) -->
        <div class="card shadow-sm mb-3 border-0 overflow-hidden">
          <button
            class="card-header d-flex justify-content-between align-items-center py-1 w-100 border-0 bg-cloudy-blue"
            :aria-expanded="!isTaskCardMinimized"
            aria-controls="taskCardBody"
            @click="isTaskCardMinimized = !isTaskCardMinimized"
          >
            <strong class="mb-0 fw-bold fs-5 text-dark-blue-grey"
              >Scenario and Task</strong
            >
            <span
              class="btn btn-sm btn-link p-0 text-decoration-none text-dark-blue-grey"
            >
              <span v-if="isTaskCardMinimized">▼ Expand</span>
              <span v-else>▲ Minimize</span>
            </span>
          </button>
          <div
            id="taskCardBody"
            v-show="!isTaskCardMinimized"
            class="card-body text-start bg-cloudy-blue py-2 px-3"
          >
            <div v-if="displayTaskDescription || displaySiteConditions.length">
              <p
                v-if="displayTaskDescription"
                class="mb-2 fw-semibold fs-6 text-dark-blue-grey"
              >
                {{ displayTaskDescription }}
              </p>
              <h6 v-if="displaySiteConditions.length" class="fw-bold mb-1 fs-6">
                Site Conditions:
              </h6>
              <ul v-if="displaySiteConditions.length" class="mb-2 ps-3 small">
                <li
                  v-for="(condition, index) in displaySiteConditions"
                  :key="index"
                >
                  {{ condition }}
                </li>
              </ul>
            </div>
            <p class="mb-1 fs-6 lh-sm">
              <span>{{ roundPromptLeadingText }}</span>
              <span
                v-if="roundPromptCallToActionText"
                class="fw-bold bg-deep-orange text-white rounded px-1"
                >{{ roundPromptCallToActionText }}</span
              >
              <span>{{ roundPromptTrailingText }}</span>
            </p>
            <small class="text-dark d-block lh-sm">
              Select {{ currentRound.correctCount }} correct species from the
              icons below.
            </small>
          </div>
        </div>

        <div class="row align-items-stretch">
          <div class="col-12 col-md-6 d-flex flex-column">
            <!-- Round Header -->
            <div
              class="card shadow-sm mb-3 border-0 overflow-hidden flex-grow-1"
            >
              <div class="card-header bg-deep-orange text-white text-start">
                <h2 class="mb-0 fw-bold">{{ currentRound.title }}</h2>
              </div>
              <div class="card-body text-start d-flex flex-column">
                <!-- Scene Image -->
                <img
                  :src="roundComplete ? nextSceneImage : currentSceneImage"
                  :alt="
                    roundComplete ? 'Thriving wetland' : 'Initial wetland site'
                  "
                  class="img-fluid rounded w-100 object-fit-contain object-position-top"
                />

                <div
                  v-if="showRoundAdvanceButton"
                  class="d-flex justify-content-center mt-3"
                >
                  <button
                    class="btn btn-primary btn-lg fw-bold"
                    @click="handleRoundContinue"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Sidebar -->
          <div class="col-12 col-md-6 d-flex flex-column">
            <!-- Species Selection Panel -->
            <div
              class="card shadow-sm mb-3 border-0 overflow-hidden flex-grow-1"
            >
              <div
                class="card-header text-start py-2 bg-deep-orange text-light"
              >
                <h6 class="mb-0">Select Species</h6>
                <small class="text-light">
                  {{ correctSelectedCount }}/{{ currentRound.correctCount }}
                  correct
                  <span v-if="incorrectSelectedCount > 0" class="text-danger">
                    · {{ incorrectSelectedCount }} wrong
                  </span>
                </small>
              </div>
              <div class="card-body p-0 bg-light-peach d-flex flex-column">
                <div class="row g-0 flex-grow-1">
                  <!-- Left column: Species list -->
                  <div class="col-12 col-lg-5 p-2 species-list-column">
                    <div class="species-list">
                      <button
                        v-for="species in currentRound.species"
                        :key="species.id"
                        class="species-list-item d-flex align-items-center w-100 text-start border rounded mb-2 p-0 bg-white overflow-hidden"
                        :class="getSpeciesCardClass(species)"
                        @click="focusSpecies(species)"
                      >
                        <!-- Debug indicator for correct answers -->
                        <span
                          v-if="isDebugMode && species.isCorrect"
                          class="debug-correct-indicator"
                          title="Correct answer (debug mode only)"
                        ></span>
                        <div
                          class="species-list-image d-flex align-items-center justify-content-center bg-light overflow-hidden flex-shrink-0"
                        >
                          <img
                            :src="getSpeciesImage(species.image)"
                            :alt="species.name"
                            class="w-100 h-100 object-fit-cover"
                          />
                        </div>
                        <div class="species-list-content flex-grow-1 px-2 py-1">
                          <div class="species-list-name fw-semibold small">
                            {{ species.name }}
                          </div>
                          <div class="species-list-status">
                            <span
                              v-if="isSpeciesSelected(species.id)"
                              class="badge badge-sm"
                              :class="
                                roundComplete
                                  ? species.isCorrect
                                    ? 'bg-success'
                                    : 'bg-danger'
                                  : 'bg-primary'
                              "
                            >
                              {{
                                roundComplete
                                  ? species.isCorrect
                                    ? "✓"
                                    : "✗"
                                  : "✓"
                              }}
                            </span>
                            <small
                              v-else
                              class="text-muted fst-italic scientific-name"
                              >{{ species.scientificName }}</small
                            >
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>

                  <!-- Right column: Species Information -->
                  <div
                    class="col-12 col-lg-7 p-3 bg-white species-info-column d-flex flex-column"
                  >
                    <!-- No species focused -->
                    <div
                      v-if="!focusedSpecies"
                      class="text-muted small h-100 d-flex align-items-center justify-content-center flex-grow-1"
                    >
                      <p class="mb-0">Click a species to learn more.</p>
                    </div>

                    <!-- Focused species info -->
                    <div
                      v-else
                      class="text-start d-flex flex-column flex-grow-1"
                    >
                      <!-- Header with image and name -->
                      <div class="mb-3">
                        <div class="text-center mb-2">
                          <img
                            :src="getSpeciesImage(focusedSpecies.image)"
                            :alt="focusedSpecies.name"
                            class="rounded w-100"
                          />
                        </div>
                        <div class="text-start">
                          <h6 class="mb-0">{{ focusedSpecies.name }}</h6>
                          <small
                            class="text-muted fst-italic scientific-name"
                            >{{ focusedSpecies.scientificName }}</small
                          >
                        </div>
                      </div>

                      <!-- Selection buttons -->
                      <div v-if="!roundComplete" class="d-grid gap-2 mb-3">
                        <button
                          v-if="!isSpeciesSelected(focusedSpecies.id)"
                          class="btn btn-primary btn-sm"
                          @click="selectFocusedSpecies"
                        >
                          ✓ Select This Species
                        </button>
                        <button
                          v-else
                          class="btn btn-outline-danger btn-sm"
                          @click="deselectFocusedSpecies"
                        >
                          ✕ Remove Selection
                        </button>
                      </div>

                      <!-- Feedback alert (shown after selection or when round complete) -->
                      <CustomAlert
                        v-if="
                          isSpeciesSelected(focusedSpecies.id) || roundComplete
                        "
                        :variant="
                          focusedSpecies.isCorrect ? 'success' : 'danger'
                        "
                        :dismissible="false"
                        class="py-2"
                      >
                        <h6 class="mb-1 small">
                          {{ focusedSpecies.feedback.title }}
                        </h6>
                        <p class="mb-0 small">
                          {{ focusedSpecies.feedback.explanation }}
                        </p>
                      </CustomAlert>

                      <!-- Species Traits -->
                      <div class="species-info-details">
                        <h6 class="mb-1 small fw-bold">Species Traits:</h6>
                        <ul class="small mb-2 ps-3">
                          <li>
                            <strong>
                              {{
                                focusedSpecies.traits.lifeCycleLabel ||
                                "Life Cycle"
                              }}:
                            </strong>
                            {{ focusedSpecies.traits.lifeCycle }}
                          </li>
                          <li>
                            <strong>
                              {{
                                focusedSpecies.traits.preferredHabitatLabel ||
                                "Habitat"
                              }}:
                            </strong>
                            {{ focusedSpecies.traits.preferredHabitat }}
                          </li>
                          <li v-if="focusedSpecies.traits.competitiveAbility">
                            <strong>
                              {{
                                focusedSpecies.traits.competitiveAbilityLabel ||
                                "Competition"
                              }}:
                            </strong>
                            {{ focusedSpecies.traits.competitiveAbility }}
                          </li>
                          <li v-if="focusedSpecies.traits.primaryHerbivore">
                            <strong>
                              {{
                                focusedSpecies.traits.primaryHerbivoreLabel ||
                                "Predator"
                              }}:
                            </strong>
                            {{ focusedSpecies.traits.primaryHerbivore }}
                          </li>
                        </ul>

                        <!-- Good/Bad Neighbors -->
                        <div
                          v-if="
                            focusedSpecies.traits.goodNeighbors?.length ||
                            focusedSpecies.traits.badNeighbors?.length
                          "
                        >
                          <h6 class="mb-1 small fw-bold">Interactions:</h6>
                          <ul class="small mb-0 ps-3">
                            <li
                              v-if="focusedSpecies.traits.goodNeighbors?.length"
                            >
                              <strong class="text-success">
                                {{
                                  focusedSpecies.traits.goodNeighborsLabel ||
                                  "Coexists with"
                                }}:
                              </strong>
                              {{
                                focusedSpecies.traits.goodNeighbors.join(", ")
                              }}
                            </li>
                            <li
                              v-if="focusedSpecies.traits.badNeighbors?.length"
                            >
                              <strong class="text-danger"
                                >{{
                                  focusedSpecies.traits.badNeighborsLabel ||
                                  "Outcompeted by"
                                }}:</strong
                              >
                              {{
                                focusedSpecies.traits.badNeighbors.join(", ")
                              }}
                            </li>
                          </ul>
                        </div>
                      </div>

                      <!-- Selection History -->
                      <div
                        v-if="selectedSpeciesForRound.length > 0"
                        class="mt-3 pt-2 border-top"
                      >
                        <h6 class="small fw-bold">
                          Your Selections ({{
                            selectedSpeciesForRound.length
                          }}):
                        </h6>
                        <ul class="list-unstyled small mb-0">
                          <li
                            v-for="speciesId in selectedSpeciesForRound"
                            :key="speciesId"
                            class="mb-1 d-flex align-items-center"
                          >
                            <span
                              class="badge me-2"
                              :class="
                                roundComplete
                                  ? getSpeciesById(speciesId)?.isCorrect
                                    ? 'bg-success'
                                    : 'bg-danger'
                                  : 'bg-primary'
                              "
                            >
                              {{
                                roundComplete
                                  ? getSpeciesById(speciesId)?.isCorrect
                                    ? "✓"
                                    : "✗"
                                  : "•"
                              }}
                            </span>
                            <button
                              type="button"
                              class="btn btn-link p-0 flex-grow-1 species-history-item text-start text-decoration-none"
                              :class="{
                                'fw-bold': focusedSpecies?.id === speciesId
                              }"
                              @click="focusSpecies(getSpeciesById(speciesId))"
                            >
                              {{ getSpeciesById(speciesId)?.name }}
                            </button>
                            <button
                              v-if="!roundComplete"
                              class="btn btn-sm btn-link text-danger p-0 ms-1"
                              @click.stop="
                                toggleSpeciesSelection(
                                  getSpeciesById(speciesId)
                                )
                              "
                              title="Remove selection"
                            >
                              ✕
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Round Complete Modal -->
    <Teleport to="body">
      <div
        v-if="showRoundCompleteModal"
        class="modal fade show d-block"
        tabindex="-1"
        aria-labelledby="roundCompleteModalLabel"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content border-success">
            <div class="modal-body text-center py-5">
              <h4
                id="roundCompleteModalLabel"
                class="fw-bold text-success mb-4"
              >
                {{ currentRound.successMessage }}
              </h4>
              <button
                class="btn btn-primary btn-lg fw-bold"
                @click="acknowledgeRoundCompletion"
              >
                Review This Round
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showRoundCompleteModal" class="modal-backdrop fade show"></div>
    </Teleport>
  </div>
</template>

<script>
import { nextTick } from "vue";
import { mapGetters, mapActions } from "vuex";
import scenesData from "@/data/scenes.json";

// Import all images from assets folder for dynamic loading
const imageModules = import.meta.glob("@/assets/*.(png|jpg|jpeg|svg)", {
  eager: true
});

// Create a map of filename to URL
const imageMap = {};
for (const path in imageModules) {
  const filename = path.split("/").pop();
  imageMap[filename] = imageModules[path].default;
}

export default {
  name: "ActivityView",
  data() {
    return {
      scenesData,
      focusedSpecies: null,
      lastSelectedSpecies: null,
      selectedManagementOption: null,
      isManagementDecisionConfirmed: false,
      roundCompletionAcknowledged: false,
      imageMap,
      isTaskCardMinimized: false,
      showProgressLegend: false
    };
  },
  computed: {
    ...mapGetters([
      "currentScenarioIndex",
      "currentRoundIndex",
      "viewingEducationalSlide",
      "viewingManagementOptions",
      "selectedSpecies",
      "completedRounds",
      "activityComplete",
      "getSelectedForRound",
      "isRoundComplete",
      "isDebugMode"
    ]),
    totalScenarios() {
      return this.scenesData.scenarios.length;
    },
    currentScenario() {
      return this.scenesData.scenarios[this.currentScenarioIndex];
    },
    currentRound() {
      return this.currentScenario.rounds[this.currentRoundIndex];
    },
    showTaskPrefix() {
      return this.currentRound.showTaskPrefix !== false;
    },
    roundPromptLeadingText() {
      return (
        this.currentRound?.promptLeadingText || this.currentRound?.prompt || ""
      );
    },
    roundPromptCallToActionText() {
      return this.currentRound?.promptCallToActionText || "";
    },
    roundPromptTrailingText() {
      return this.currentRound?.promptTrailingText || "";
    },
    totalRoundsInScenario() {
      return this.currentScenario.rounds.length;
    },
    displayTaskDescription() {
      if (this.currentRound.description) {
        return this.currentRound.description;
      }
      return this.currentRoundIndex === 0
        ? this.currentScenario.description
        : "";
    },
    displaySiteConditions() {
      if (Array.isArray(this.currentRound.conditions)) {
        return this.currentRound.conditions;
      }
      return this.currentRoundIndex === 0
        ? this.currentScenario.conditions
        : [];
    },
    selectedSpeciesForRound() {
      return this.getSelectedForRound(this.currentRound.id);
    },
    correctSelectedCount() {
      return this.selectedSpeciesForRound.filter((id) => {
        const species = this.currentRound.species.find((s) => s.id === id);
        return species && species.isCorrect;
      }).length;
    },
    incorrectSelectedCount() {
      return this.selectedSpeciesForRound.filter((id) => {
        const species = this.currentRound.species.find((s) => s.id === id);
        return species && !species.isCorrect;
      }).length;
    },
    roundComplete() {
      // Round is complete only when all correct species are selected AND no incorrect ones
      return (
        this.correctSelectedCount === this.currentRound.correctCount &&
        this.incorrectSelectedCount === 0
      );
    },
    isLastRoundInScenario() {
      return this.currentRoundIndex >= this.totalRoundsInScenario - 1;
    },
    isLastScenario() {
      return this.currentScenarioIndex >= this.totalScenarios - 1;
    },
    isFinalActivityRound() {
      return this.isLastScenario && this.isLastRoundInScenario;
    },
    hasManagementOptions() {
      return !!this.currentScenario.managementOptions;
    },
    managementOptionsForDisplay() {
      const options = this.currentScenario.managementOptions?.options || [];
      if (options.length <= 1) return options;
      return [...options.slice(1), options[0]];
    },
    preferredManagementOptionId() {
      return this.currentScenario.managementOptions?.preferredOptionId || null;
    },
    isPreferredManagementOption() {
      return (
        !!this.preferredManagementOptionId &&
        this.selectedManagementOption === this.preferredManagementOptionId
      );
    },
    preferredManagementOptionMessage() {
      return (
        this.currentScenario.managementOptions?.preferredOptionMessage || ""
      );
    },
    educationalSlideContinueText() {
      // Scenario 2, after Round 1: Go to management options
      if (
        this.currentScenarioIndex === 1 &&
        this.currentRoundIndex === 0 &&
        this.hasManagementOptions
      ) {
        return "Continue to Management Decision →";
      }
      // Last scenario: show completion screen
      if (this.isLastScenario) {
        return "View Final Summary →";
      }
      // Otherwise: Go to next scenario
      return "Continue to Next Scenario →";
    },
    showEducationalSpeciesHighlights() {
      const highlights =
        this.currentScenario.educationalSlide?.speciesHighlights;
      if (!highlights?.length) {
        return false;
      }

      // Hide highlights on Scenario 2's post-round-1 educational slide.
      return !(this.currentScenarioIndex === 1 && this.currentRoundIndex === 0);
    },
    showRoundCompleteModal() {
      return (
        this.roundComplete &&
        !this.isFinalActivityRound &&
        !this.roundCompletionAcknowledged &&
        !this.viewingEducationalSlide &&
        !this.viewingManagementOptions
      );
    },
    showRoundAdvanceButton() {
      return (
        this.roundComplete &&
        (this.roundCompletionAcknowledged || this.isFinalActivityRound) &&
        !this.viewingEducationalSlide &&
        !this.viewingManagementOptions
      );
    },
    // Get the current scene image based on scenario and round progress
    currentSceneImage() {
      if (this.currentRound.image && this.imageMap[this.currentRound.image]) {
        return this.imageMap[this.currentRound.image];
      }

      const scenarioNum = this.currentScenarioIndex + 1; // 1 or 2

      // Scenario 2 has an extra management decision step between rounds 1 and 2,
      // so Round 2 visuals should be shifted forward by one state image.
      const scenarioTwoStateOffset =
        this.currentScenarioIndex === 1 &&
        this.hasManagementOptions &&
        this.currentRoundIndex >= 1
          ? 1
          : 0;

      // state1 = initial, state2 = after round 1, state3 = after round 2, state4 = after round 3
      const stateNum = Math.min(
        4,
        this.currentRoundIndex + 1 + scenarioTwoStateOffset
      );
      const imageName =
        stateNum === 1
          ? `s${scenarioNum}_initial.jpg`
          : `s${scenarioNum}_state${stateNum}.jpg`;
      return this.imageMap[imageName] || this.imageMap["placeholder.png"] || "";
    },
    // Get the next scene image (shown when round is complete)
    nextSceneImage() {
      const scenarioNum = this.currentScenarioIndex + 1;

      const scenarioTwoStateOffset =
        this.currentScenarioIndex === 1 &&
        this.hasManagementOptions &&
        this.currentRoundIndex >= 1
          ? 1
          : 0;

      const nextStateNum = Math.min(
        4,
        this.currentRoundIndex + 2 + scenarioTwoStateOffset
      ); // +2 because we want the next state
      const imageName = `s${scenarioNum}_state${nextStateNum}.jpg`;
      return (
        this.imageMap[imageName] ||
        this.imageMap[`s${scenarioNum}_state4.jpg`] ||
        this.imageMap["healthy.png"] ||
        ""
      );
    },
    // Completion image for the activity complete screen
    completionImage() {
      return (
        this.imageMap["s2_state4.jpg"] || this.imageMap["healthy.png"] || ""
      );
    },
    // Educational slide image - shows the final state of the current scenario
    educationalSlideImage() {
      const scenarioNum = this.currentScenarioIndex + 1;
      const slideConfig = this.currentScenario.educationalSlide || {};
      const roundOverrideImage = slideConfig.imageByRound
        ? slideConfig.imageByRound[String(this.currentRoundIndex)]
        : null;

      if (roundOverrideImage && this.imageMap[roundOverrideImage]) {
        return this.imageMap[roundOverrideImage];
      }

      if (slideConfig.image && this.imageMap[slideConfig.image]) {
        return this.imageMap[slideConfig.image];
      }

      // Educational slide shows the final state (state4) for the scenario
      const imageName = `s${scenarioNum}_state4.jpg`;
      return this.imageMap[imageName] || this.imageMap["healthy.png"] || "";
    },
    educationalSlideTitle() {
      const slideConfig = this.currentScenario.educationalSlide || {};
      const roundTitleOverride = slideConfig.titleByRound
        ? slideConfig.titleByRound[String(this.currentRoundIndex)]
        : null;
      return roundTitleOverride || slideConfig.title || "";
    },
    educationalSlideContent() {
      const slideConfig = this.currentScenario.educationalSlide || {};
      const roundContentOverride = slideConfig.contentByRound
        ? slideConfig.contentByRound[String(this.currentRoundIndex)]
        : null;
      return roundContentOverride || slideConfig.content || "";
    },
    educationalSlideParagraphs() {
      return this.splitContentIntoParagraphs(this.educationalSlideContent);
    },
    completionFinalEducationParagraphs() {
      const finalEducationContent =
        this.scenesData.completionMessage?.finalEducation?.content || "";
      return finalEducationContent
        .split(/\n+/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);
    }
  },
  watch: {
    currentScenarioIndex() {
      this.scrollToTopAfterRender();
    },
    currentRoundIndex() {
      this.scrollToTopAfterRender();
    },
    viewingEducationalSlide() {
      this.scrollToTopAfterRender();
    },
    viewingManagementOptions() {
      this.scrollToTopAfterRender();
    },
    activityComplete() {
      this.scrollToTopAfterRender();
    },
    roundComplete(newVal) {
      if (newVal) {
        this.$store.dispatch("completeRound", this.currentRound.id);
        this.roundCompletionAcknowledged = false;
      }
    },
    "currentRound.id"() {
      this.roundCompletionAcknowledged = false;
    }
  },
  methods: {
    ...mapActions([
      "toggleSpecies",
      "completeRound",
      "resetActivity",
      "resetRound",
      "nextRound",
      "setActivityComplete",
      "setViewingEducationalSlide",
      "setViewingManagementOptions",
      "finishEducationalSlide",
      "finishManagementOptions",
      "setCurrentScenario",
      "setCurrentRound"
    ]),
    navigateToScenario(scenarioIndex) {
      if (!this.isDebugMode) return;
      this.showProgressLegend = false;
      this.setCurrentScenario(scenarioIndex);
      this.setCurrentRound(0);
      this.setViewingEducationalSlide(false);
      this.setViewingManagementOptions(false);
    },
    navigateToRound(roundIndex) {
      if (!this.isDebugMode) return;
      this.showProgressLegend = false;
      this.setCurrentRound(roundIndex);
      this.setViewingEducationalSlide(false);
      this.setViewingManagementOptions(false);
    },
    getProgressBadgeClasses(isComplete, isActive) {
      return {
        "progress-badge-complete": isComplete,
        "progress-badge-incomplete": !isComplete,
        "progress-badge-active": isActive
      };
    },
    getProgressBadgeAriaLabel(type, index, isComplete, isActive) {
      const completionLabel = isComplete ? "complete" : "incomplete";
      const activeLabel = isActive ? "active" : "inactive";
      return `${type} ${index}: ${completionLabel}, ${activeLabel}`;
    },
    scrollToTop() {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    },
    scrollToTopAfterRender() {
      nextTick(() => {
        this.scrollToTop();
      });
    },
    splitContentIntoParagraphs(content) {
      if (!content) return [];

      const normalizedContent = content.replace(/\s+/g, " ").trim();
      if (!normalizedContent) return [];

      const words = normalizedContent.split(" ");
      const paragraphs = [];
      let currentSentence = "";

      words.forEach((word) => {
        currentSentence = currentSentence ? `${currentSentence} ${word}` : word;

        if (/[.!?]["']?$/.test(word)) {
          paragraphs.push(currentSentence.trim());
          currentSentence = "";
        }
      });

      if (currentSentence) {
        paragraphs.push(currentSentence.trim());
      }

      return paragraphs.filter(Boolean);
    },
    toggleSpeciesSelection(species) {
      if (!species) return;
      this.$store.dispatch("toggleSpecies", {
        roundId: this.currentRound.id,
        speciesId: species.id
      });
      this.lastSelectedSpecies = species;
    },
    focusSpecies(species) {
      if (!species) return;
      this.focusedSpecies = species;
    },
    selectFocusedSpecies() {
      if (!this.focusedSpecies || this.roundComplete) return;
      if (!this.isSpeciesSelected(this.focusedSpecies.id)) {
        this.toggleSpeciesSelection(this.focusedSpecies);
      }
    },
    deselectFocusedSpecies() {
      if (!this.focusedSpecies || this.roundComplete) return;
      if (this.isSpeciesSelected(this.focusedSpecies.id)) {
        this.toggleSpeciesSelection(this.focusedSpecies);
      }
    },
    isSpeciesSelected(speciesId) {
      return this.selectedSpeciesForRound.includes(speciesId);
    },
    getSpeciesById(speciesId) {
      return this.currentRound.species.find((s) => s.id === speciesId);
    },
    getSpeciesImage(imageName) {
      return this.imageMap[imageName] || this.imageMap["placeholder.png"] || "";
    },
    getSpeciesCardClass(species) {
      const isFocused = this.focusedSpecies?.id === species.id;
      const isSelected = this.isSpeciesSelected(species.id);

      // Show correct/incorrect colors when round is complete
      if (this.roundComplete && isSelected) {
        return species.isCorrect
          ? "border-success bg-success bg-opacity-10"
          : "border-danger bg-danger bg-opacity-10";
      }

      // Selected species (keep white background)
      if (isSelected) {
        return isFocused ? "border-primary species-focused" : "border-primary";
      }

      // Focused but not selected (keep white background)
      if (isFocused) {
        return "species-focused";
      }

      return "border-secondary species-selectable";
    },
    isScenarioComplete(scenarioIndex) {
      const scenario = this.scenesData.scenarios[scenarioIndex];
      return scenario.rounds.every((round) =>
        this.completedRounds.includes(round.id)
      );
    },
    acknowledgeRoundCompletion() {
      if (!this.roundComplete) return;
      this.roundCompletionAcknowledged = true;
    },
    handleRoundContinue() {
      this.lastSelectedSpecies = null;
      this.focusedSpecies = null;
      this.roundCompletionAcknowledged = false;
      this.isTaskCardMinimized = false;

      // Scenario 2, Round 1: Show educational slide first (then management options)
      if (
        this.currentScenarioIndex === 1 &&
        this.currentRoundIndex === 0 &&
        this.hasManagementOptions
      ) {
        this.setViewingEducationalSlide(true);
        return;
      }

      // Last round in scenario: Show educational slide
      if (this.isLastRoundInScenario) {
        this.setViewingEducationalSlide(true);
        return;
      }

      // Otherwise: Go to next round
      this.nextRound({
        totalRoundsInScenario: this.totalRoundsInScenario,
        totalScenarios: this.totalScenarios,
        hasManagementOptions: this.hasManagementOptions
      });
    },
    handleEducationalSlideContinue() {
      this.isTaskCardMinimized = false;
      // Scenario 2, after Round 1 educational slide: Show management options
      if (
        this.currentScenarioIndex === 1 &&
        this.currentRoundIndex === 0 &&
        this.hasManagementOptions
      ) {
        this.selectedManagementOption = null;
        this.isManagementDecisionConfirmed = false;
        this.setViewingEducationalSlide(false);
        this.setViewingManagementOptions(true);
        return;
      }

      this.finishEducationalSlide({
        totalScenarios: this.totalScenarios,
        hasManagementOptions: false
      });
    },
    selectManagementOption(optionId) {
      this.selectedManagementOption = optionId;
      this.isManagementDecisionConfirmed = false;
    },
    confirmManagementDecision() {
      if (!this.isPreferredManagementOption) return;
      this.isManagementDecisionConfirmed = true;
    },
    getSelectedManagementExplanation() {
      if (
        !this.selectedManagementOption ||
        !this.currentScenario.managementOptions
      ) {
        return "";
      }
      const option = this.currentScenario.managementOptions.options.find(
        (o) => o.id === this.selectedManagementOption
      );
      return option ? option.explanation : "";
    },
    handleManagementContinue() {
      if (!this.isManagementDecisionConfirmed) return;
      this.selectedManagementOption = null;
      this.isManagementDecisionConfirmed = false;
      this.isTaskCardMinimized = false;
      this.finishManagementOptions();
    },
    resetCurrentRound() {
      this.resetRound(this.currentRound.id);
      this.lastSelectedSpecies = null;
      this.focusedSpecies = null;
      this.roundCompletionAcknowledged = false;
      this.isTaskCardMinimized = false;
    },
    startOver() {
      this.resetActivity();
      this.lastSelectedSpecies = null;
      this.focusedSpecies = null;
      this.selectedManagementOption = null;
      this.isManagementDecisionConfirmed = false;
      this.roundCompletionAcknowledged = false;
      this.scrollToTopAfterRender();
    }
  },
  mounted() {
    this.scrollToTopAfterRender();
  }
};
</script>

<style scoped>
.species-card {
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  cursor: pointer;
}

.species-selectable:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.species-focused {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Species list styles for sidebar */
.species-list-item {
  cursor: pointer;
  position: relative;
  transition:
    transform 0.15s,
    box-shadow 0.15s;
}

/* Debug indicator for correct answers */
.debug-correct-indicator {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 12px;
  height: 12px;
  background-color: #28a745;
  border: 2px solid #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  z-index: 10;
}

.species-list-item:hover {
  transform: translateX(2px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.species-list-item.species-focused {
  border-color: #ff5722 !important;
  border-width: 2px !important;
}

.species-list-item.species-selected {
  background-color: rgba(13, 110, 253, 0.05);
}

.species-list-image {
  width: 80px;
  height: 70px;
}

.species-list-name {
  line-height: 1.2;
  margin-top: 0.15rem;
  margin-bottom: 0.15rem;
}

.species-list-status .badge-sm {
  font-size: 0.7rem;
  padding: 0.2em 0.5em;
}

.species-image-container {
  height: 180px;
}

.species-history-item {
  cursor: pointer;
}

.species-history-item:hover {
  text-decoration: underline;
}

.management-option {
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
}

.management-option:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.management-option-selected {
  border: 2px solid #d73f09 !important;
  box-shadow: 0 4px 12px rgba(215, 63, 9, 0.3) !important;
  background-color: #ffffff !important;
}

/* Integrated species panel styles */
.species-list-column {
  max-height: 500px;
  overflow-y: auto;
}

.species-info-column {
  border-left: 1px solid #dee2e6;
  min-height: 200px;
  flex-grow: 1;
}

.species-info-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
}

.species-info-image-lg {
  width: 200px;
  height: 200px;
  object-fit: cover;
}

.species-info-details {
  font-size: 0.875rem;
}

.scientific-name {
  line-height: 0.95 !important;
  display: block;
  margin-bottom: 0.15rem;
}

.notable-species-image-container {
  height: 120px;
  padding: 0.5rem;
}

.notable-species-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.notable-species-card .card-body {
  padding-top: 0.35rem !important;
  padding-bottom: 0.5rem !important;
}

.notable-species-card .card-title {
  line-height: 1.15;
}

.notable-species-card .scientific-name {
  margin-bottom: 0;
  line-height: 1.05 !important;
}

.progress-state-badge {
  min-width: 2rem;
  border: 1px solid transparent;
  border-radius: 0.7rem;
}

.progress-badge-incomplete {
  background-color: #ffffff;
  color: #0e2433;
  border-color: #9aa4ad;
}

.progress-badge-complete {
  background-color: #198754;
  color: #ffffff;
  border-color: #146c43;
}

.progress-badge-active {
  box-shadow: 0 0 0 2px #ffc107;
}

.progress-legend {
  position: absolute;
  top: calc(100% + 0.25rem);
  right: 0;
  width: 13rem;
  z-index: 20;
}

.progress-help-button {
  width: 1.9rem;
  height: 1.9rem;
  min-width: 1.9rem;
  min-height: 1.9rem;
  border-radius: 50%;
  padding: 0;
  line-height: 1;
}

@media (max-width: 991.98px) {
  .species-list-column {
    max-height: none;
    overflow-y: visible;
    border-bottom: 1px solid #dee2e6;
  }

  .species-info-column {
    border-left: none;
  }
}

/* .placeholder-image uses bg-secondary Bootstrap class */
</style>
